import { useGetApStamplessContractsSuspenseInfinite } from '@/context/services/ap_stampless_contract/ApStamplessContract.service';
import { useTranslation } from '@/i18n';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';

type Options = {
  label: string;
  value: string;
};

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<FormSelectProps<TFieldValues>['selectProps'], 'options'>;
  per?: number;
  validateMaxCount?: number;
  defaultOptions?: Options[];
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'>;

const _ContractNumberSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  per = 50,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const { data, isLoading, hasNextPage, fetchNextPage } =
    useGetApStamplessContractsSuspenseInfinite({ query, per });
  const options = useMemo(() => {
    if (!data) return defaultOptions;
    return data.pages.flatMap(
      (page) =>
        page.data.ap_stampless_contracts?.map((item) => {
          const partnerName = item.contract_partners?.[0]?.name ?? '';
          const label = `${item.contract_number ?? ''}（${
            item.contract_name ?? ''
          }/${partnerName}）`;
          return {
            label,
            value: item.contract_number || '',
          };
        }) ?? []
    );
  }, [data, defaultOptions]);
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const selectProps: SelectProps = useMemo(() => {
    return {
      onSearchDebounceWait: 600,
      comboBox: true,
      placeholder: t('contract_number_placeholder'),
      inputPlaceholder: t('contract_number_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      fetchMore: fetchMore,
      loading: isLoading,
      selectAll: undefined,
      selectAllLabel: undefined,
      mode: 'multiple',
      additionalOption: true,
    };
  }, [_selectProps, isLoading, options, t, onSearch, fetchMore]);

  return <FormSelect<TFieldValues> selectProps={selectProps} {...rest} />;
};
_ContractNumberSelect.displayName = '_ContractNumberSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ContractNumberSelect = memo((props) => (
  <_ContractNumberSelect {...props} />
)) as typeof _ContractNumberSelect;
ContractNumberSelect.displayName = 'ContractNumberSelect';
